// Seitennavigation, IMG_4896 bis IMG_4899 und IMG_4924 bis IMG_4926
// (01.09.2026): Menueblock, Laenderkennzeichen-Kategorie, Laenderliste mit
// aktivem Land, Stift-Symbol oeffnet "Länder sortieren".

import { useState } from "react";
import { Clock, Star, Settings, BarChart3, Flag, Pencil } from "lucide-react";
import { Link } from "react-router-dom";
import type { Land } from "../lib/bundle.js";
import type { LandSichtbarkeit } from "../lib/einstellungen.js";
import { LAENDERKENNZEICHEN_MODUS, vollstaendigeReihenfolge } from "../lib/treffer.js";
import { Flaggenband } from "./Flaggenband.js";
import { LaenderSortieren } from "./LaenderSortieren.js";

const menue = [
  { to: "/letzte-kennzeichen", label: "Letzte Kennzeichen", Icon: Clock },
  { to: "/statistik", label: "Statistik", Icon: Star },
  { to: "/einstellungen", label: "Einstellungen", Icon: Settings },
  { to: "/leaderboard", label: "Leaderboard", Icon: BarChart3 },
];

export function Drawer({
  offen,
  onClose,
  laender,
  nutzerReihenfolge,
  sichtbarkeit,
  aktivesLand,
  onLandWaehlen,
  onReihenfolgeAendern,
  onSichtbarkeitAendern,
}: {
  offen: boolean;
  onClose: () => void;
  laender: Land[];
  nutzerReihenfolge: string[];
  sichtbarkeit: Record<string, LandSichtbarkeit>;
  aktivesLand: string;
  onLandWaehlen: (iso2OderModus: string) => void;
  onReihenfolgeAendern: (neu: string[]) => void;
  onSichtbarkeitAendern: (iso2: string, naechste: LandSichtbarkeit) => void;
}) {
  const [sortierenOffen, setSortierenOffen] = useState(false);

  if (!offen) return null;

  const landByIso2 = new Map(laender.map((l) => [l.iso2, l]));
  const reihenfolge = vollstaendigeReihenfolge(laender, nutzerReihenfolge);
  const sichtbareLaender = reihenfolge
    .map((iso2) => landByIso2.get(iso2))
    .filter((l): l is Land => l !== undefined && (sichtbarkeit[l.iso2]?.list ?? true));

  return (
    <>
      <div className="fixed inset-0 z-20 bg-black/40" onClick={onClose} />
      <nav className="fixed inset-y-0 left-0 z-20 w-72 max-w-[85vw] overflow-y-auto bg-flaeche-inhalt shadow-lg">
        <div className="flex justify-end p-2">
          <button
            type="button"
            onClick={() => setSortierenOffen(true)}
            aria-label="Länder sortieren"
            className="flex min-h-11 min-w-11 items-center justify-center text-neutral-600"
          >
            <Pencil size={20} />
          </button>
        </div>

        <ul>
          {menue.map(({ to, label, Icon }) => (
            <li key={to}>
              <Link
                to={to}
                onClick={onClose}
                className="flex min-h-11 items-center gap-4 px-4 py-2 text-sm text-neutral-800 hover:bg-neutral-50"
              >
                <Icon size={20} className="text-neutral-500" />
                {label}
              </Link>
            </li>
          ))}
          <li>
            <button
              type="button"
              onClick={() => {
                onLandWaehlen(LAENDERKENNZEICHEN_MODUS);
                onClose();
              }}
              className={`flex min-h-11 w-full items-center gap-4 px-4 py-2 text-left text-sm hover:bg-neutral-50 ${
                aktivesLand === LAENDERKENNZEICHEN_MODUS ? "font-medium text-akzent" : "text-neutral-800"
              }`}
            >
              <Flag size={20} className={aktivesLand === LAENDERKENNZEICHEN_MODUS ? "text-akzent" : "text-neutral-500"} />
              Länderkennzeichen
            </button>
          </li>
        </ul>

        <ul className="border-t border-neutral-200">
          {sichtbareLaender.map((land) => (
            <li key={land.iso2}>
              <button
                type="button"
                onClick={() => {
                  onLandWaehlen(land.iso2);
                  onClose();
                }}
                className={`flex min-h-11 w-full items-center gap-3 px-4 py-2 text-left text-sm hover:bg-neutral-50 ${
                  aktivesLand === land.iso2 ? "font-medium text-akzent" : "text-neutral-800"
                }`}
              >
                {/* Schmaler, hoher Streifen wie in der Original-Seitenleiste
                    (01.09.2026), nicht quadratisch - dasselbe
                    Seitenverhaeltnis wie das grosse Band in
                    KennzeichenGrafik.tsx (56x96), nur kleiner. */}
                <Flaggenband land={land} breite={24} hoehe={40} sternGroesse={16} schriftKlasse="text-[9px]" gerundet />
                {land.nameDe}
              </button>
            </li>
          ))}
        </ul>
      </nav>

      <LaenderSortieren
        offen={sortierenOffen}
        onClose={() => setSortierenOffen(false)}
        laender={laender}
        reihenfolge={reihenfolge}
        sichtbarkeit={sichtbarkeit}
        onReihenfolgeAendern={onReihenfolgeAendern}
        onSichtbarkeitAendern={onSichtbarkeitAendern}
      />
    </>
  );
}
